import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Award, Megaphone, Presentation } from "lucide-react";
import Seo from "@/components/Seo";
import { MICE_IMG } from "@/lib/media";

const MICE_BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "MICE", path: "/mice" },
];

const SERVICES = [
  {
    icon: Briefcase,
    title: "Corporate Meetings & Offsites",
    copy: "Board retreats, leadership workshops, and team offsites — from 20 to 200 pax, across any of our 7 destinations.",
  },
  {
    icon: Award,
    title: "Dealer & Sales Incentives",
    copy: "Reward your top performers with meticulously produced incentive travel — themed gala dinners, exclusive access, and once-in-a-lifetime moments.",
  },
  {
    icon: Presentation,
    title: "Global Conferences",
    copy: "Multi-day conferences with keynote production, breakout management, simultaneous interpretation, and on-site mobile-app logistics.",
  },
  {
    icon: Megaphone,
    title: "Product Launches & Experiential",
    copy: "Stage your product moment with creative concept, venue sourcing, AV, entertainment, and press hospitality.",
  },
];

const PROCESS = [
  ["01", "Brief", "Share the objective, pax, dates, and budget. Or talk to TrawellAI for a first estimate."],
  ["02", "Proposal", "Within 48 hrs we return a themed proposal — hotels, venues, creative concept, costing."],
  ["03", "Production", "Contracts, site inspections, rooming, transport, AV, content, F&B, VIP handling."],
  ["04", "On-Ground", "A dedicated ops manager plus local team runs the floor. You enjoy the event."],
];

export default function Mice() {
  return (
    <div data-testid="mice-page">
      <Seo
        path="/mice"
        title="MICE Specialists — Meetings, Incentives, Conferences & Events across Asia & Middle East"
        description="Trawelldmc is the MICE-first DMC for South East Asia & West Asia. 70% of our volume is MICE. From 40-pax board offsites to 2,500-pax dealer conferences in Dubai, Singapore, Bangkok, KL, Ho Chi Minh, Hong Kong, Manila."
        breadcrumb={MICE_BREADCRUMB}
      />
      <section className="relative">
        <img
          src={MICE_IMG}
          alt="Trawelldmc MICE event in Dubai — corporate conference production"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">MICE Specialists · 20 Years</div>
            <h1 className="font-heading text-5xl md:text-7xl font-light tracking-tight leading-[1.02]">
              Meetings. Incentives. <br />
              Conferences. <span className="text-[#FF5238]">Events.</span>
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              70% of our volume is MICE. From a 40-pax board offsite to a 2,500-pax dealer conference,
              we're the operator Fortune 500s and ambitious mid-markets trust to deliver.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inquiry?type=MICE" className="btn-primary" data-testid="mice-rfp-btn">
                Submit an RFP <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="mice-ai-quote-btn">
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Impact stats */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 grid grid-cols-2 md:grid-cols-4 gap-8">
        {[
          ["2,500", "Largest group delivered"],
          ["140,000+", "Total pax managed"],
          ["07", "Active destinations"],
          ["48 hrs", "Average proposal turnaround"],
        ].map(([v, l]) => (
          <div key={l} className="border-l-2 border-[#FF5238] pl-5">
            <div className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">{v}</div>
            <div className="overline mt-2">{l}</div>
          </div>
        ))}
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-24 md:pb-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we do</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Four disciplines. <br />One accountable partner.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8" data-testid={`mice-service-${s.title.split(" ")[0].toLowerCase()}`}>
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-2xl font-medium tracking-tight text-[#0B192C] mt-5">{s.title}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">How we work</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              From brief to breakdown — <br />in four moves.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {PROCESS.map(([n, t, c]) => (
              <div key={n} className="border-t border-white/20 pt-6">
                <div className="font-heading text-6xl font-light text-[#FF5238]">{n}</div>
                <div className="font-heading text-xl font-medium mt-3">{t}</div>
                <p className="text-slate-300 text-sm mt-3 leading-relaxed">{c}</p>
              </div>
            ))}
          </div>
          <div className="mt-16">
            <Link to="/inquiry?type=MICE" className="btn-primary" data-testid="mice-bottom-cta">
              Start your MICE brief <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
